import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./Table";
import { User, UserRole } from '@/types';
import { useToast } from '@/contexts/ToastContext';
import { API_BASE_URL } from '@/config';
import { useLogger } from '@/contexts/LogContext';
import { LogLevel, LogCategory } from '@/types/logs';
import { Loader2, Search } from 'lucide-react';
import { useWallet } from '@/contexts/WalletContext';
import { contractAddresses } from '@/data/contractAddresses';
import KalaKrutGovernorABI from '@/abis/KalaKrutGovernor.json';
import { ethers } from 'ethers';

const ActionButton: React.FC<{ onClick: () => void; className: string; children: React.ReactNode }> = ({ onClick, className, children }) => (
  <button
    onClick={onClick}
    className={`text-xs font-semibold px-2.5 py-1 rounded-md transition-colors ${className}`}>
    {children}
  </button>
);

const TableActions: React.FC<{ 
  member: User;
  role: UserRole; 
  onAction: (action: string, memberId: string) => void;
}> = ({ member, role, onAction }) => {
  const handleAction = (action: string) => onAction(action, member.id);
  const isAdmin = role === UserRole.ADMIN || role === UserRole.SYSTEM_ADMIN_LIVE;
  
  if (isAdmin) {
    return (
        <div className="space-x-2">
          <ActionButton onClick={() => handleAction('remove')} className="bg-red-500/20 text-red-300 hover:bg-red-500/40">Remove</ActionButton>
        </div>
      );
  }
  return <></>;
};

const DAOMemberRealDataTable = ({ currentUserRole, currentUserId }: { currentUserRole: UserRole, currentUserId: string }) => {
  const [allMembers, setAllMembers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [isFallback, setIsFallback] = useState(false);
  const { notify } = useToast();
  const { addLog } = useLogger();
  const { provider, isConnected } = useWallet();
  const PAGE_SIZE = 10;

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setIsFallback(false);
    addLog("Starting DAO member data fetch.", LogLevel.INFO, LogCategory.DATA);

    if (provider && isConnected) {
      try {
        const governorContract = new ethers.Contract(contractAddresses.KalaKrutGovernor, KalaKrutGovernorABI.abi, provider);
        const memberAddresses = await governorContract.getVoters();
        
        const memberPromises = memberAddresses.map(async (address: string) => {
          const response = await fetch(`${API_BASE_URL}/api/users?walletAddress=${address}`, { credentials: 'include' });
          if (response.ok) return response.json();
          // Return a shell user object if API lookup fails
          return { id: address, walletAddress: address, name: 'On-Chain Member', role: UserRole.DAO_MEMBER, email: 'N/A' };
        });

        const members = await Promise.all(memberPromises);
        setAllMembers(members.flat()); // .flat() in case the API returns an array for a user
        addLog(`Fetched ${members.length} on-chain DAO members.`, LogLevel.SUCCESS, LogCategory.DATA);
        setIsLoading(false);
        return;
      } catch (e: unknown) {
        const onChainError = "Could not load on-chain member data. Falling back to API.";
        addLog(`On-chain fetch failed: ${(e as Error).message}`, LogLevel.ERROR, LogCategory.DATA);
        setError(onChainError);
        notify(onChainError, 'warn');
        setIsFallback(true);
      }
    }

    // Fallback to Off-Chain API
    try {
      let apiData;
      const response = await fetch(`${API_BASE_URL}/api/users?role=DAO_MEMBER`, { credentials: 'include' });

      if (response.ok) {
        apiData = await response.json();
        addLog(`Fetched ${apiData.length} members from REAL API as fallback.`, LogLevel.INFO, LogCategory.API);
      } else {
        addLog(`Real API fallback failed, using mock data.`, LogLevel.WARN, LogCategory.API);
        const mockResponse = await fetch('/dao-members.json');
        if (!mockResponse.ok) throw new Error('Real API and mock data both failed.');
        apiData = await mockResponse.json();
        notify('Displaying mock data. Wallet not connected and API failed.', 'warn');
      }
      
      if (Array.isArray(apiData)) {
        setAllMembers(apiData);
        setIsFallback(true);
      } else {
        throw new Error('Fetched off-chain data is not an array.');
      }

    } catch (e: unknown) {
      const fallbackError = `Critical: Failed to load any member data: ${(e as Error).message}`;
      addLog(fallbackError, LogLevel.CRITICAL, LogCategory.API);
      setError(fallbackError);
      notify(fallbackError, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [provider, isConnected, notify, addLog]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAction = async (action: string, memberId: string) => {
    if (action === 'remove') {
      if (!window.confirm('Are you sure you want to remove this member? This action cannot be undone.')) return;
      addLog(`Admin ${currentUserId} initiated removal of DAO member ${memberId}`, LogLevel.INFO, LogCategory.DAO);
      try {
        const response = await fetch(`${API_BASE_URL}/api/users/${memberId}/dao-membership`, { method: 'DELETE', credentials: 'include' });
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to remove member via API.');
        }
        setAllMembers(current => current.filter(m => m.id !== memberId));
        notify(`Member ${memberId} has been removed from the DAO.`, 'success');
        addLog(`DAO Member ${memberId} successfully removed by ${currentUserId}.`, LogLevel.INFO, LogCategory.DAO);
      } catch (err) {
        notify((err as Error).message, 'error');
        addLog(`Error removing DAO member ${memberId}: ${(err as Error).message}`, LogLevel.ERROR, LogCategory.DAO);
      }
    } else {
      notify(`Action '${action}' for member '${memberId}' is not implemented.`, 'info');
    }
  };

  const filteredMembers = useMemo(() => {
    return allMembers.filter(m => 
      (m.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (m.walletAddress || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [allMembers, searchTerm]);

  const displayedMembers = useMemo(() => {
      return filteredMembers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  }, [filteredMembers, page]);

  const hasMore = page * PAGE_SIZE < filteredMembers.length;

  if (isLoading && allMembers.length === 0) return (
    <div className="flex justify-center items-center h-48">
      <Loader2 className="w-8 h-8 text-kala-secondary animate-spin" />
      <p className="ml-4 text-kala-400">Loading DAO members...</p>
    </div>
  );

  return (
    <div className="bg-kala-900 text-white rounded-lg p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">DAO Members</h2>
        <div className="relative">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-kala-500" />
            <input
                type="text"
                placeholder="Search by name or address..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="bg-kala-800 border border-kala-700 rounded-full pl-10 pr-4 py-2 text-sm text-white focus:ring-1 focus:ring-kala-secondary outline-none w-64"
            />
        </div>
      </div>
      {error && <div className="p-4 mb-4 bg-yellow-500/10 text-yellow-400 text-center text-sm rounded-lg">Warning: {error}</div>}
      {(!isConnected || isFallback) && (
        <div className="p-2 mb-4 bg-kala-900/30 text-kala-500 text-center text-[10px] uppercase tracking-widest border-y border-kala-700">
          Note: Displaying fallback data. For real-time information, connect your wallet.
        </div>
      )}

      <Table>
        <TableCaption>A list of all DAO members. {allMembers.length === 0 && !isLoading && "No members found."}</TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Wallet Address</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {displayedMembers.map((member) => (
            <TableRow key={member.id}>
              <TableCell>{member.name || 'N/A'}</TableCell>
              <TableCell>{member.email}</TableCell>
              <TableCell>{member.walletAddress}</TableCell>
              <TableCell className="text-right">
                <TableActions member={member} role={currentUserRole} onAction={handleAction} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

        {displayedMembers.length === 0 && !isLoading && (
            <div className="text-center p-8 text-kala-500">
                <p>No members found for your search term.</p>
            </div>
        )}

      <div className="flex justify-between items-center mt-6">
        <button 
          onClick={() => setPage(p => Math.max(1, p - 1))}
          disabled={page === 1 || isLoading}
          className="px-4 py-2 bg-kala-700 hover:bg-kala-600 text-white font-bold rounded-lg disabled:opacity-50 transition-colors"
        >
          Previous
        </button>
        <span className="text-kala-400">Page {page} of {Math.ceil(filteredMembers.length / PAGE_SIZE)}</span>
        <button 
          onClick={() => setPage(p => p + 1)}
          disabled={!hasMore || isLoading}
          className="px-4 py-2 bg-kala-700 hover:bg-kala-600 text-white font-bold rounded-lg disabled:opacity-50 transition-colors"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default DAOMemberRealDataTable;
